import React, { useState, useRef, useCallback, useEffect } from 'react';
import styles from './DiagramEditor.module.css';
import { Shape, Connection, Point, ShapeType, TextElement, FreeLine } from '../../types/diagram.types';
import { Toolbar } from '../../components/Diagrams/Toolbar';
import { Canvas } from '../../components/Diagrams/Canvas';
import { PropertiesPanel } from '../../components/Diagrams/PropertiesPanel';
import { ConfirmDialog } from '../../components/Diagrams/ConfirmDialog/ConfirmDialog';
import { DEFAULT_SHAPE_PRESET } from '../../config/shapePresets';
import { useLanguage } from '../../context/LanguageContext';

const DiagramEditor: React.FC = () => {
  const { t } = useLanguage();
  
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedShape, setSelectedShape] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<Point>({ x: 0, y: 0 });
  const [connecting, setConnecting] = useState<string | null>(null);
  const [tempConnection, setTempConnection] = useState<Point | null>(null);
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  
  const [texts, setTexts] = useState<TextElement[]>([]);
  const [selectedText, setSelectedText] = useState<string | null>(null);
  const [draggingText, setDraggingText] = useState<string | null>(null);
  const [textDragOffset, setTextDragOffset] = useState<Point>({ x: 0, y: 0 });
  
  const canvasRef = useRef<HTMLDivElement>(null);
  const [freeLines, setFreeLines] = useState<FreeLine[]>([]);
  const [selectedLine, setSelectedLine] = useState<string | null>(null);
  const [drawingLine, setDrawingLine] = useState<FreeLine | null>(null);
  const [tool, setTool] = useState<'select' | 'line'>('select');
  const isDrawingRef = useRef(false);

  const [isEditMode, setIsEditMode] = useState(true);

  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    type: 'shape' | 'connection' | 'text' | 'line' | 'clearAll' | null;
    id: string | null;
  }>({
    isOpen: false,
    type: null,
    id: null,
  });

  const STORAGE_KEY = 'diagram-editor-state-v1';

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    try {
      const data = JSON.parse(raw);

      setShapes(data.shapes ?? []);
      setConnections(data.connections ?? []);
      setTexts(data.texts ?? []);
      setFreeLines(data.freeLines ?? []);
      setPan(data.pan ?? { x: 0, y: 0 });
      setZoom(data.zoom ?? 1);
    } catch {
      console.warn('Diagram state corrupted, ignoring');
    }
  }, []);

  useEffect(() => {
    const data = {
      shapes,
      connections,
      texts,
      freeLines,
      pan,
      zoom,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [shapes, connections, texts, freeLines, pan, zoom]);

  const addShape = useCallback((type: ShapeType) => {
    if (!isEditMode) return;
    
    const newShape: Shape = {
        id: `shape-${Date.now()}`,
        type,
        position: { x: 200 - pan.x / zoom, y: 200 - pan.y / zoom },
        size: { width: 120, height: 80 },
        stylePreset: DEFAULT_SHAPE_PRESET, 
        style: undefined,                  
        text: 'Новый элемент',
        fontSize: 14,
        rotation: 0,
    };
    setShapes(prev => [...prev, newShape]);
    setSelectedShape(newShape.id);
    setSelectedText(null);
    setSelectedLine(null);
    setTool('select');
  }, [pan, zoom, isEditMode]);

  const [resizing, setResizing] = useState<{
    shapeId: string;
    direction: 'se' | 'sw' | 'ne' | 'nw';
    startMouse: Point;
    startSize: { width: number; height: number };
    startPos: Point;
    } | null>(null);

    useEffect(() => {
        if (!resizing || !isEditMode) return;

        const onMove = (e: MouseEvent) => {
            const dx = (e.clientX - resizing.startMouse.x) / zoom;
            const dy = (e.clientY - resizing.startMouse.y) / zoom;

            setShapes(prev =>
                prev.map(s => {
                    if (s.id !== resizing.shapeId) return s;

                    let { width, height } = resizing.startSize;
                    let { x, y } = resizing.startPos;

                    if (resizing.direction.includes('e')) width = Math.max(40, width + dx);
                    if (resizing.direction.includes('s')) height = Math.max(30, height + dy);
                    if (resizing.direction.includes('w')) {
                        width = Math.max(40, width - dx);
                        x += dx;
                    }
                    if (resizing.direction.includes('n')) {
                        height = Math.max(30, height - dy);
                        y += dy;
                    }

                    return {
                        ...s,
                        size: { width, height },
                        position: { x, y },
                    };
                })
            );
        };

        const onUp = () => {
            setResizing(null);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);

        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [resizing, zoom, isEditMode]);

  const getDistance = (p1: Point, p2: Point): number => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const isPointNearLine = (point: Point, line: FreeLine, threshold: number = 10): boolean => {
    for (let i = 0; i < line.points.length - 1; i++) {
      const p1 = line.points[i];
      const p2 = line.points[i + 1];
      
      const lineLength = getDistance(p1, p2);
      if (lineLength === 0) continue;
      
      const t = Math.max(0, Math.min(1, 
        ((point.x - p1.x) * (p2.x - p1.x) + (point.y - p1.y) * (p2.y - p1.y)) / (lineLength * lineLength)
      ));
      
      const projection = {
        x: p1.x + t * (p2.x - p1.x),
        y: p1.y + t * (p2.y - p1.y)
      };
      
      const distance = getDistance(point, projection);
      if (distance <= threshold) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (!isDrawingRef.current || !canvasRef.current) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDrawingRef.current || !drawingLine) return;

      const rect = canvasRef.current!.getBoundingClientRect();
      const p = {
        x: (e.clientX - rect.left - pan.x) / zoom,
        y: (e.clientY - rect.top - pan.y) / zoom,
      };

      const lastPoint = drawingLine.points[drawingLine.points.length - 1];
      const distance = getDistance(lastPoint, p);
      
      if (distance > 3) { 
        setDrawingLine(prev => 
          prev ? { ...prev, points: [...prev.points, p] } : null
        );
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDrawingRef.current && drawingLine) {
        if (drawingLine.points.length > 1) {
          setFreeLines(prev => [...prev, drawingLine]);
        }
        setDrawingLine(null);
        isDrawingRef.current = false;
        setTool('select');
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [drawingLine, pan, zoom]);

  const handleMouseDown = useCallback((e: React.MouseEvent, shapeId: string) => {
    e.stopPropagation();
    
    if (e.button === 1 || (e.button === 0 && e.ctrlKey)) {
      return;
    }

    if (!isEditMode) {
      setSelectedShape(shapeId);
      setSelectedConnection(null);
      setSelectedText(null);
      setSelectedLine(null);
      return;
    }

    setSelectedShape(shapeId);
    setSelectedConnection(null);
    setSelectedText(null);
    setSelectedLine(null);
    
    if (e.shiftKey) {
      setConnecting(shapeId);
    } else {
      const shape = shapes.find(s => s.id === shapeId);
      if (shape) {
        setDragging(shapeId);
        const rect = (e.target as HTMLElement).getBoundingClientRect();
        setDragOffset({
          x: (e.clientX - rect.left) / zoom,
          y: (e.clientY - rect.top) / zoom,
        });
      }
    }
  }, [shapes, zoom, isEditMode]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (draggingText) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const newX = (e.clientX - rect.left - pan.x) / zoom - textDragOffset.x;
      const newY = (e.clientY - rect.top - pan.y) / zoom - textDragOffset.y;
      
      setTexts(prev => prev.map(text => 
        text.id === draggingText
          ? { ...text, position: { x: newX, y: newY } }
          : text
      ));
      return;
    }

    if (resizing) {
      return; 
    }

    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPan({ x: pan.x + dx, y: pan.y + dy });
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }

    if (dragging) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const newX = (e.clientX - rect.left - pan.x) / zoom - dragOffset.x;
      const newY = (e.clientY - rect.top - pan.y) / zoom - dragOffset.y;
      
      setShapes(prev => prev.map(shape => 
        shape.id === dragging
          ? { ...shape, position: { x: newX, y: newY } }
          : shape
      ));

      updateConnectionsForShape(dragging, { x: newX, y: newY });
    } else if (connecting) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      
      setTempConnection({
        x: (e.clientX - rect.left - pan.x) / zoom,
        y: (e.clientY - rect.top - pan.y) / zoom,
      });
    }
  }, [dragging, draggingText, connecting, dragOffset, textDragOffset, pan, zoom, isPanning, panStart]);

  const handleMouseUp = useCallback((e: React.MouseEvent, targetShapeId?: string) => {
    setResizing(null);
    setDraggingText(null);

    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (connecting && targetShapeId && targetShapeId !== connecting) {
      const fromShape = shapes.find(s => s.id === connecting);
      const toShape = shapes.find(s => s.id === targetShapeId);
      
      if (fromShape && toShape) {
        const newConnection: Connection = {
          id: `conn-${Date.now()}`,
          from: connecting,
          to: targetShapeId,
          fromPoint: getShapeCenter(fromShape),
          toPoint: getShapeCenter(toShape),
          type: 'arrow',
          color: '#1e3a8a',
          width: 2,
        };
        setConnections(prev => [...prev, newConnection]);
      }
    }
    
    setDragging(null);
    setConnecting(null);
    setTempConnection(null);
  }, [connecting, shapes, isPanning]);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if (tool === 'line' && isEditMode) {
      const rect = canvasRef.current!.getBoundingClientRect();

      const p = {
        x: (e.clientX - rect.left - pan.x) / zoom,
        y: (e.clientY - rect.top - pan.y) / zoom,
      };

      setDrawingLine({
        id: `line-${Date.now()}`,
        points: [p],
        color: '#111827',
        width: 2,
      });
      
      isDrawingRef.current = true;
      return;
    }

    if (e.button === 1 || (e.button === 0 && e.ctrlKey) || e.button === 2) {
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      setSelectedShape(null);
      setSelectedConnection(null);
      setSelectedText(null);
      setSelectedLine(null);
    } else if (e.button === 0) {
      const rect = canvasRef.current!.getBoundingClientRect();
      const clickPoint = {
        x: (e.clientX - rect.left - pan.x) / zoom,
        y: (e.clientY - rect.top - pan.y) / zoom,
      };

      let lineClicked = false;
      for (const line of freeLines) {
        if (isPointNearLine(clickPoint, line, 10 / zoom)) {
          setSelectedLine(line.id);
          setSelectedShape(null);
          setSelectedConnection(null);
          setSelectedText(null);
          lineClicked = true;
          break;
        }
      }

      if (!lineClicked) {
        setSelectedShape(null);
        setSelectedConnection(null);
        setSelectedText(null);
        setSelectedLine(null);
      }
    }
  }, [tool, pan, zoom, freeLines, isEditMode]);

  const handleResizeStart = useCallback((
    e: React.MouseEvent,
    shapeId: string,
    direction: 'se' | 'sw' | 'ne' | 'nw'
  ) => {
    if (!isEditMode) return;
    
    e.stopPropagation();

    const shape = shapes.find(s => s.id === shapeId);
    if (!shape) return;

    setResizing({
      shapeId,
      direction,
      startMouse: { x: e.clientX, y: e.clientY },
      startSize: { ...shape.size },
      startPos: { ...shape.position },
    });
  }, [shapes, isEditMode]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.1, Math.min(3, zoom * delta));
    setZoom(newZoom);
  }, [zoom]);

  const getShapeCenter = (shape: Shape): Point => {
    return {
      x: shape.position.x + shape.size.width / 2,
      y: shape.position.y + shape.size.height / 2,
    };
  };

  const updateConnectionsForShape = (shapeId: string, newPosition: Point) => {
    const shape = shapes.find(s => s.id === shapeId);
    if (!shape) return;

    const center = {
      x: newPosition.x + shape.size.width / 2,
      y: newPosition.y + shape.size.height / 2,
    };

    setConnections(prev => prev.map(conn => {
      if (conn.from === shapeId) {
        return { ...conn, fromPoint: center };
      } else if (conn.to === shapeId) {
        return { ...conn, toPoint: center };
      }
      return conn;
    }));
  };

  const deleteShape = useCallback((shapeId: string) => {
    setShapes(prev => prev.filter(s => s.id !== shapeId));
    setConnections(prev => prev.filter(c => c.from !== shapeId && c.to !== shapeId));
    setSelectedShape(null);
  }, []);

  const deleteConnection = useCallback((connId: string) => {
    setConnections(prev => prev.filter(c => c.id !== connId));
    setSelectedConnection(null);
  }, []);

  const updateShape = useCallback((shapeId: string, updates: Partial<Shape>) => {
    setShapes(prev => prev.map(shape => 
      shape.id === shapeId ? { ...shape, ...updates } : shape
    ));
  }, []);

  const updateConnection = useCallback((connId: string, updates: Partial<Connection>) => {
    setConnections(prev => prev.map(conn => 
      conn.id === connId ? { ...conn, ...updates } : conn
    ));
  }, []);

  const handleTextMouseDown = useCallback((e: React.MouseEvent, textId: string) => {
    e.stopPropagation();
    
    if (e.button === 1 || (e.button === 0 && e.ctrlKey)) {
      return;
    }

    setSelectedText(textId);
    setSelectedShape(null);
    setSelectedConnection(null);
    setSelectedLine(null);

    if (!isEditMode) return;

    const text = texts.find(t => t.id === textId);
    if (text) {
      setDraggingText(textId);
      const rect = (e.target as HTMLElement).getBoundingClientRect();
      setTextDragOffset({
        x: (e.clientX - rect.left) / zoom,
        y: (e.clientY - rect.top) / zoom,
      });
    }
  }, [texts, zoom, isEditMode]);

  const handleTextDoubleClick = useCallback((textId: string) => {
    if (!isEditMode) return;
    
    const text = texts.find(t => t.id === textId);
    if (text) {
      const newText = prompt('Введите текст:', text.text);
      if (newText !== null) {
        updateText(textId, { text: newText });
      }
    }
  }, [texts, isEditMode]);

  const updateText = useCallback((textId: string, updates: Partial<TextElement>) => {
    setTexts(prev => prev.map(text => 
      text.id === textId ? { ...text, ...updates } : text
    ));
  }, []);

  const deleteText = useCallback((textId: string) => {
    setTexts(prev => prev.filter(t => t.id !== textId));
    setSelectedText(null);
  }, []);

  const addText = useCallback(() => {
    if (!isEditMode) return;
    
    const text: TextElement = {
      id: `text-${Date.now()}`,
      type: 'text',
      position: {
        x: 200 - pan.x / zoom,
        y: 200 - pan.y / zoom,
      },
      text: 'Текст',
      fontSize: 16,
      color: '#111827',
    };

    setTexts(prev => [...prev, text]);
    setSelectedText(text.id);
    setSelectedShape(null);
    setSelectedConnection(null);
    setSelectedLine(null);
    setTool('select');
  }, [pan, zoom, isEditMode]);

  const addFreeLine = useCallback(() => {
    if (!isEditMode) return;
    
    setTool('line');
    setSelectedShape(null);
    setSelectedConnection(null);
    setSelectedText(null);
    setSelectedLine(null);
  }, [isEditMode]);

  const updateFreeLine = useCallback((lineId: string, updates: Partial<FreeLine>) => {
    setFreeLines(prev => prev.map(line => 
      line.id === lineId ? { ...line, ...updates } : line
    ));
  }, []);

  const deleteFreeLine = useCallback((lineId: string) => {
    setFreeLines(prev => prev.filter(l => l.id !== lineId));
    setSelectedLine(null);
  }, []);

  const clearAll = useCallback(() => {
    if (!isEditMode) return;
    
    setConfirmDelete({ isOpen: true, type: 'clearAll', id: null });
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setSelectedShape(null);
      setSelectedConnection(null);
      setSelectedText(null);
      setSelectedLine(null);
    }
  }, [isEditMode]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (confirmDelete.isOpen) return;
      
      if (e.key === 'Delete') {
        if (selectedShape) {
          setConfirmDelete({ isOpen: true, type: 'shape', id: selectedShape });
        } else if (selectedConnection) {
          setConfirmDelete({ isOpen: true, type: 'connection', id: selectedConnection });
        } else if (selectedText) {
          setConfirmDelete({ isOpen: true, type: 'text', id: selectedText });
        } else if (selectedLine) {
          setConfirmDelete({ isOpen: true, type: 'line', id: selectedLine });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedShape, selectedConnection, selectedText, selectedLine, confirmDelete.isOpen]);

  const handleConfirmDelete = useCallback(() => {
    const { type, id } = confirmDelete;
    if (!type) return;

    switch (type) {
      case 'shape':
        if (id) deleteShape(id);
        break;
      case 'connection':
        if (id) deleteConnection(id);
        break;
      case 'text':
        if (id) deleteText(id);
        break;
      case 'line':
        if (id) deleteFreeLine(id);
        break;
      case 'clearAll':
        setShapes([]);
        setConnections([]);
        setTexts([]);
        setFreeLines([]);
        setSelectedShape(null);
        setSelectedConnection(null);
        setSelectedText(null);
        setSelectedLine(null);
        setTool('select');
        break;
    }

    setConfirmDelete({ isOpen: false, type: null, id: null });
  }, [confirmDelete, deleteShape, deleteConnection, deleteText, deleteFreeLine]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDelete({ isOpen: false, type: null, id: null });
  }, []);

  const handleDeleteClick = useCallback((
    type: 'shape' | 'connection' | 'text' | 'line',
    id: string
  ) => {
    setConfirmDelete({ isOpen: true, type, id });
  }, []);

  const getDeleteMessage = useCallback(() => {
    switch (confirmDelete.type) {
      case 'shape':
        return t('confirmDeleteShape');
      case 'connection':
        return t('confirmDeleteConnection');
      case 'text':
        return t('confirmDeleteText');
      case 'line':
        return t('confirmDeleteLine');
      case 'clearAll':
        return t('confirmClearAll');
      default:
        return '';
    }
  }, [confirmDelete.type, t]);

  const exportToJSON = useCallback(() => {
    const data = {
      shapes,
      connections,
      texts,
      freeLines,
      version: '1.0',
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagram-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [shapes, connections, texts, freeLines]);

  const importFromJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = JSON.parse(event.target?.result as string);
            setShapes(data.shapes || []);
            setConnections(data.connections || []);
            setTexts(data.texts || []);
            setFreeLines(data.freeLines || []);
            setTool('select');
          } catch (error) {
            alert('Ошибка загрузки файла');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, []);

  const selectedShapeData = shapes.find(s => s.id === selectedShape);
  const selectedConnectionData = connections.find(c => c.id === selectedConnection);
  const selectedTextData = texts.find(t => t.id === selectedText);
  const selectedLineData = freeLines.find(l => l.id === selectedLine);

  return (
    <div className={styles.container}>
      <Toolbar
        onAddShape={addShape}
        onAddText={addText}
        onAddLine={addFreeLine}
        onClear={clearAll}
        onExport={exportToJSON}
        onImport={importFromJSON}
        zoom={zoom}
        onZoomIn={() => setZoom(z => Math.min(3, z * 1.1))}
        onZoomOut={() => setZoom(z => Math.max(0.1, z / 1.1))}
        onZoomReset={() => setZoom(1)}
        isEditMode={isEditMode}
        onToggleEditMode={() => setIsEditMode(prev => !prev)}
      />
      
      <div className={styles.mainContent}>
        <Canvas
          ref={canvasRef}
          shapes={shapes}
          texts={texts} 
          freeLines={freeLines}
          drawingLine={drawingLine}
          connections={connections}
          selectedShape={selectedShape}
          selectedConnection={selectedConnection}
          selectedText={selectedText}
          selectedLine={selectedLine}
          connecting={connecting}
          tempConnection={tempConnection}
          pan={pan}
          zoom={zoom}
          tool={tool}
          isEditMode={isEditMode}
          onMouseDown={handleCanvasMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onWheel={handleWheel}
          onShapeMouseDown={handleMouseDown}
          onShapeMouseUp={handleMouseUp}
          onResizeStart={handleResizeStart}
          onConnectionClick={setSelectedConnection}
          onTextMouseDown={handleTextMouseDown}
          onTextDoubleClick={handleTextDoubleClick}
          onShapeDoubleClick={(shapeId) => {
            if (!isEditMode) return;
            const shape = shapes.find(s => s.id === shapeId);
            if (shape) {
              const newText = prompt('Введите текст:', shape.text);
              if (newText !== null) {
                updateShape(shapeId, { text: newText });
              }
            }
          }}
        />
        
        {isEditMode && (selectedShapeData || selectedConnectionData || selectedTextData || selectedLineData) && (
          <PropertiesPanel
            selectedShape={selectedShapeData}
            selectedConnection={selectedConnectionData}
            selectedText={selectedTextData}
            selectedLine={selectedLineData}
            isEditMode={isEditMode}
            onUpdateShape={updateShape}
            onUpdateConnection={updateConnection}
            onUpdateText={updateText}
            onUpdateLine={updateFreeLine}
            onDeleteShape={(id) => handleDeleteClick('shape', id)}
            onDeleteConnection={(id) => handleDeleteClick('connection', id)}
            onDeleteText={(id) => handleDeleteClick('text', id)}
            onDeleteLine={(id) => handleDeleteClick('line', id)}
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        title={t('confirmDelete')}
        message={getDeleteMessage()}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        danger={true}
      />
    </div>
  );
};

export default DiagramEditor;